import {Icon, Typography, cn} from '@sollapay/ui';

import {BALANCE_HOVER_MOTION_CLASS} from '../config';
import {getActiveBalance} from '../utils';

import type {TrustStatus} from '@sollapay/enums';

const AMOUNT_CLASS = 'text-lg leading-6';

export interface TrustAccountBalanceChipProps {
  status: TrustStatus;
  balance?: string | null;
  className?: string;
}

/** ₪0 keeps the wallet but is muted, so real balances stand out; no balance (inactive or missing) is a light dash. */
export function TrustAccountBalanceChip({
  status,
  balance,
  className
}: TrustAccountBalanceChipProps) {
  const amount = getActiveBalance(status, balance);

  if (!amount) {
    return (
      <Typography
        as="span"
        weight="medium"
        data-slot="trust-account-balance-empty"
        className={cn(AMOUNT_CLASS, 'text-fg-disabled', className)}
      >
        —
      </Typography>
    );
  }

  const isZero = amount.isZero();

  return (
    <div
      data-slot="trust-account-balance-chip"
      className={cn('flex w-fit items-center gap-2 rounded-lg px-2 py-1', className)}
    >
      <Icon
        name="wallet"
        className={cn(
          'shrink-0 transition-[color,translate] duration-350 motion-reduce:transition-none',
          isZero
            ? 'text-fg-quaternary group-hover/row:text-fg-tertiary'
            : 'text-fg group-hover/row:-translate-y-px group-hover/row:text-brand-accent',
          'size-4'
        )}
      />
      <div className="flex flex-1 items-end gap-1">
        <Typography
          as="span"
          weight={isZero ? 'medium' : 'semibold'}
          className={cn(
            'relative inline-block overflow-visible tabular-nums transition-[scale,color] ltr:origin-left rtl:origin-right',
            BALANCE_HOVER_MOTION_CLASS,
            isZero
              ? 'text-fg-quaternary group-hover/row:text-fg-tertiary'
              : 'text-fg group-hover/row:scale-106',
            AMOUNT_CLASS
          )}
        >
          {amount.formatNIS({symbol: true})}
          {!isZero && (
            <span
              aria-hidden
              data-slot="balance-underline"
              className={cn(
                'absolute -bottom-1.25 start-0 h-0.75 w-0 rounded-full bg-progress-fill transition-[width] group-hover/row:w-7',
                BALANCE_HOVER_MOTION_CLASS
              )}
            />
          )}
        </Typography>
      </div>
    </div>
  );
}
